// Offline-tolerant upload queue, persisted in IndexedDB so a closed tab or a
// dead signal doesn't lose anything. Items run strictly in order:
//   file (one per photo/video) -> complete (one Stream post per batch)  |  note
// It only drains while the app is open (iOS gives web apps no background sync),
// which the banner makes explicit to the user.
import { api } from '../api.js';

const DB_NAME = 'onsite-outbox';
const STORE = 'items';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const out = fn(t.objectStore(STORE));
    t.oncomplete = () => { db.close(); resolve(out?.result); };
    t.onerror = () => { db.close(); reject(t.error); };
  });
}

const dbAll = () => tx('readonly', (s) => s.getAll());
const dbPut = (item) => tx('readwrite', (s) => s.put(item));
const dbDel = (id) => tx('readwrite', (s) => s.delete(id));

// ---- in-memory mirror ---------------------------------------------------------
let items = [];
let authRequired = false;
let snapshot = { items: [], authRequired: false };
const listeners = new Set();
let loaded = false;
let running = false;
let timer = null;

function emit() {
  snapshot = { items: items.slice(), authRequired };
  listeners.forEach((l) => l());
}
let lastProgressEmit = 0;
function emitProgress() {
  const t = Date.now();
  if (t - lastProgressEmit > 150) { lastProgressEmit = t; emit(); }
}

export const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const getSnapshot = () => snapshot;

// Strip the blob out of what the UI sees.
export const label = (i) =>
  i.kind === 'file' ? i.name : i.kind === 'complete' ? 'Posting to the job' : i.kind === 'goods' ? 'Goods note' : 'Note';

async function load() {
  if (loaded) return;
  loaded = true;
  try {
    const stored = await dbAll();
    items = stored
      .map((i) => ({ ...i, state: i.state === 'failed' ? 'failed' : 'queued', progress: 0 }))
      .sort((a, b) => a.seq - b.seq);
  } catch (_) { items = []; }
  emit();
}

let seqCounter = Date.now();

export async function enqueue(list) {
  await load();
  for (const base of list) {
    const item = { state: 'queued', tries: 0, nextAt: 0, error: null, progress: 0, ...base, seq: seqCounter++ };
    items.push(item);
    await dbPut(item).catch(() => {});
  }
  emit();
  pump();
}

export async function retry(id) {
  const i = items.find((x) => x.id === id);
  if (!i) return;
  Object.assign(i, { state: 'queued', tries: 0, nextAt: 0, error: null });
  await dbPut(i).catch(() => {});
  emit();
  pump();
}

export async function discard(id) {
  items = items.filter((x) => x.id !== id);
  await dbDel(id).catch(() => {});
  emit();
  pump();
}

async function remove(item) {
  items = items.filter((x) => x.id !== item.id);
  await dbDel(item.id).catch(() => {});
}

// A "complete" must wait for every file in its batch to have gone up (or been discarded).
const blocked = (i) => (i.kind === 'complete' || i.kind === 'goods') && items.some((x) => x.kind === 'file' && x.sid === i.sid);

function next() {
  const now = Date.now();
  return items.find((i) => i.state === 'queued' && i.nextAt <= now && !blocked(i));
}

const RETRYABLE = (status) => status === 0 || status === 408 || status === 429 || status >= 500;

function messageFor(e) {
  const code = e.data?.error || e.message;
  switch (code) {
    case 'folder_missing': return "This job's NextCloud folder doesn't exist yet. Tell the office.";
    case 'no_folder': return 'This job has no NextCloud folder linked. Tell the office.';
    case 'too_large': return `That file is too big (limit ${e.data?.maxMb || ''} MB).`;
    case 'unsupported_type': return "That type of file can't be uploaded.";
    case 'job_not_found': return "That job isn't available any more.";
    case 'network': return 'No signal – will retry.';
    default: return 'Something went wrong – will retry.';
  }
}

async function run(item) {
  const base = `/api/jobs/${item.jobId}`;
  if (item.kind === 'file') {
    await api.putFile(
      `${base}/files?sid=${item.sid}&fid=${item.id}${item.query ? `&${item.query}` : ''}`,
      item.blob,
      item.mime,
      (p) => { item.progress = p; emitProgress(); }
    );
  } else if (item.kind === 'complete') {
    try {
      await api.postQuiet(`${base}/files/complete`, { sid: item.sid, caption: item.caption || '' });
    } catch (e) {
      if (e.data?.error === 'no_files') return; // every file was discarded -- nothing to post
      throw e;
    }
  } else if (item.kind === 'goods') {
    await api.postQuiet(`${base}/goods`, { sid: item.sid, direction: item.direction, text: item.text });
  } else if (item.kind === 'note') {
    await api.postQuiet(`${base}/notes`, { sid: item.sid, text: item.text });
  }
}

async function pump() {
  await load();
  if (running) return;
  running = true;
  clearTimeout(timer);
  try {
    let item;
    while ((item = next())) {
      item.state = 'uploading';
      item.progress = 0;
      emit();
      try {
        await run(item);
        authRequired = false;
        await remove(item);
      } catch (e) {
        if (e.status === 401) {
          // Session expired: keep everything, ask the user to sign in again.
          item.state = 'queued';
          item.nextAt = Date.now() + 5000;
          authRequired = true;
          emit();
          break;
        }
        item.tries += 1;
        item.error = messageFor(e);
        const retryable = (RETRYABLE(e.status) || e.data?.error === 'exists') && item.tries < 10;
        if (retryable) {
          item.state = 'queued';
          item.nextAt = Date.now() + Math.min(60000, 2000 * 2 ** (item.tries - 1));
        } else {
          item.state = 'failed';
        }
        await dbPut({ ...item, progress: 0 }).catch(() => {});
      }
      emit();
    }
  } finally {
    running = false;
    emit();
    const waiting = items.filter((i) => i.state === 'queued').map((i) => i.nextAt);
    if (waiting.length) timer = setTimeout(pump, Math.max(1000, Math.min(...waiting) - Date.now()));
  }
}

export const kick = () => pump();

if (typeof window !== 'undefined') {
  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && kick());
  setInterval(kick, 15000);
  kick();
}
